import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Key, Copy, RefreshCw, Check, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { user } from '../api';

const ApiKeyPage = () => {
  const [apiKey, setApiKey] = useState(null);
  const [createdAt, setCreatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    loadApiKey();
  }, []);

  const loadApiKey = async () => {
    try {
      const data = await user.getApiKey();
      setApiKey(data.apiKey);
      setCreatedAt(data.createdAt);
    } catch (error) {
      console.error('Failed to load API key:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!apiKey) return;
    await navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const data = await user.regenerateApiKey();
      setApiKey(data.apiKey);
      setCreatedAt(data.createdAt);
      setShowConfirm(false);
    } catch (error) {
      console.error('Failed to regenerate API key:', error);
    } finally {
      setRegenerating(false);
    }
  };

  const maskApiKey = (key) => {
    if (!key) return '';
    return key.substring(0, 6) + '•'.repeat(20) + key.substring(key.length - 4);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-white mb-2">API Key</h1>
        <p className="text-white/60">
          Your API key is used to authenticate widget requests from your website.
        </p>
      </motion.div>

      {/* API Key Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-[#111118] rounded-2xl p-6 border border-white/5 mb-6"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center">
            <Key className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Your API Key</h2>
            {createdAt && (
              <p className="text-sm text-white/40">
                Created {new Date(createdAt).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>

        {apiKey ? (
          <>
            <div className="bg-[#0a0a0f] rounded-xl p-4 mb-4 flex items-center gap-3">
              <code className="flex-1 font-mono text-sm text-blue-400 break-all">
                {showKey ? apiKey : maskApiKey(apiKey)}
              </code>
              <button
                onClick={() => setShowKey(!showKey)}
                className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                title={showKey ? 'Hide' : 'Show'}
              >
                {showKey ? (
                  <EyeOff className="w-4 h-4 text-white/60" />
                ) : (
                  <Eye className="w-4 h-4 text-white/60" />
                )}
              </button>
              <button
                onClick={handleCopy}
                className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                title="Copy"
              >
                {copied ? (
                  <Check className="w-4 h-4 text-emerald-400" />
                ) : (
                  <Copy className="w-4 h-4 text-white/60" />
                )}
              </button>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowConfirm(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Regenerate Key
              </button>
            </div>
          </>
        ) : (
          <div className="text-center py-8">
            <p className="text-white/60 mb-4">No API key generated yet.</p>
            <button
              onClick={handleRegenerate}
              disabled={regenerating}
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold hover:from-blue-600 hover:to-cyan-600 transition-all disabled:opacity-50"
            >
              {regenerating ? 'Generating...' : 'Generate API Key'}
            </button>
          </div>
        )}
      </motion.div>

      {/* Warning Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-amber-900/20 rounded-xl p-4 border border-amber-500/20 flex items-start gap-3"
      >
        <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="font-medium text-amber-300 mb-1">Keep your API key secret</h3>
          <p className="text-sm text-amber-300/70">
            Never share your API key publicly or commit it to version control.
            The key is validated along with your whitelisted domains for security.
          </p>
        </div>
      </motion.div>

      {/* Confirm Dialog */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#111118] rounded-2xl p-6 max-w-md w-full border border-white/10"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <h3 className="text-lg font-bold text-white">Regenerate API Key?</h3>
            </div>
            <p className="text-white/60 mb-6">
              This will invalidate your current API key immediately. Any widgets using the old key
              will stop working until you update them with the new key.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 px-4 py-2.5 rounded-xl bg-white/5 text-white hover:bg-white/10 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRegenerate}
                disabled={regenerating}
                className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-50"
              >
                {regenerating ? 'Regenerating...' : 'Regenerate'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ApiKeyPage;

